// ── Crosshair Customization System ──
// Configurable crosshair with dynamic spread, multiple styles, and outline support.
// Renders on a canvas overlay for performance.

// Re-export types from the standalone config module (no circular deps)
export { type CrosshairConfig, DEFAULT_CROSSHAIR_CONFIG } from './crosshair-config';
import { type CrosshairConfig } from './crosshair-config';

import { settingsStore } from '../core/settings';

// ---------------------------------------------------------------------------
// Render data types
// ---------------------------------------------------------------------------

/** Data structure describing what to draw on the canvas. */
export interface CrosshairLine {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CrosshairDot {
  radius: number;
}

export interface CrosshairCircle {
  radius: number;
  thickness: number;
}

export interface CrosshairRenderData {
  style: 'classic' | 'dot' | 'circle';
  color: string;
  alpha: number;
  outline: boolean;
  outlineThickness: number;
  lines?: CrosshairLine[];
  dot?: CrosshairDot;
  circle?: CrosshairCircle;
}

// ---------------------------------------------------------------------------
// Gap calculation
// ---------------------------------------------------------------------------

/** Pixels-per-radian multiplier for dynamic crosshair expansion. */
const SPREAD_TO_PX = 200;

/**
 * Compute the effective crosshair gap (in px) given the current weapon spread.
 * When `dynamic` is true, the gap expands linearly with spread.
 */
export function computeCrosshairGap(config: CrosshairConfig, spread: number): number {
  if (!config.dynamic) return config.gap;
  return Math.max(0, config.gap + spread * SPREAD_TO_PX);
}

// ---------------------------------------------------------------------------
// Render data generation
// ---------------------------------------------------------------------------

/**
 * Build a CrosshairRenderData struct from config + current spread.
 * This is consumed by the canvas crosshair renderer each frame.
 */
export function generateCrosshairRenderData(
  config: CrosshairConfig,
  spread: number,
): CrosshairRenderData {
  const effectiveGap = computeCrosshairGap(config, spread);
  const base: CrosshairRenderData = {
    style: config.style,
    color: config.color,
    alpha: config.alpha,
    outline: config.outline,
    outlineThickness: config.outlineThickness,
  };

  switch (config.style) {
    case 'classic': {
      const halfThick = config.thickness / 2;
      base.lines = [
        // Top
        { x: -halfThick, y: -(effectiveGap + config.size), width: config.thickness, height: config.size },
        // Bottom
        { x: -halfThick, y: effectiveGap, width: config.thickness, height: config.size },
        // Left
        { x: -(effectiveGap + config.size), y: -halfThick, width: config.size, height: config.thickness },
        // Right
        { x: effectiveGap, y: -halfThick, width: config.size, height: config.thickness },
      ];
      if (config.dot) {
        base.dot = { radius: config.thickness };
      }
      break;
    }
    case 'dot': {
      base.dot = { radius: Math.max(1, config.thickness + 1) };
      break;
    }
    case 'circle': {
      base.circle = {
        radius: effectiveGap + config.size / 2,
        thickness: config.thickness,
      };
      break;
    }
  }

  return base;
}

// ---------------------------------------------------------------------------
// Canvas crosshair renderer
// ---------------------------------------------------------------------------

/**
 * Manages a canvas overlay that renders the crosshair each frame.
 * Replaces the static CSS crosshair from index.html.
 */
export class CrosshairRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private currentSpread = 0;
  private boundResize = () => this.resize();

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'crosshair-canvas';
    this.canvas.style.cssText = [
      'position: fixed',
      'inset: 0',
      'pointer-events: none',
      'z-index: 30',
    ].join('; ');
    this.ctx = this.canvas.getContext('2d')!;

    // Attach to <body> (not #hud) so safe-area-inset padding on mobile doesn't
    // shift the crosshair off viewport center. Render logic assumes the canvas
    // spans the full viewport and uses window.innerWidth/innerHeight for center.
    document.body.appendChild(this.canvas);

    // Size to window
    this.resize();
    window.addEventListener('resize', this.boundResize);

    // Hide the old CSS crosshair
    const oldCrosshair = document.querySelector('.crosshair') as HTMLElement | null;
    if (oldCrosshair) {
      oldCrosshair.style.display = 'none';
    }
  }

  private resize(): void {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = window.innerWidth * dpr;
    this.canvas.height = window.innerHeight * dpr;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /** Set the current weapon spread (radians) for dynamic crosshair. */
  setSpread(spread: number): void {
    this.currentSpread = spread;
  }

  /** Show the crosshair canvas. */
  show(): void {
    this.canvas.style.display = '';
  }

  /** Hide the crosshair canvas (e.g. during spectator mode). */
  hide(): void {
    this.canvas.style.display = 'none';
  }

  /** Render one frame. Call from the game loop. */
  render(): void {
    const config = settingsStore.get().crosshair;
    const data = generateCrosshairRenderData(config, this.currentSpread);

    const w = window.innerWidth;
    const h = window.innerHeight;
    const cx = w / 2;
    const cy = h / 2;

    this.ctx.clearRect(0, 0, w, h);
    this.ctx.globalAlpha = data.alpha;

    if (data.style === 'classic' && data.lines) {
      this.renderClassic(data, cx, cy);
    } else if (data.style === 'dot' && data.dot) {
      this.renderDot(data, cx, cy);
    } else if (data.style === 'circle' && data.circle) {
      this.renderCircle(data, cx, cy);
    }

    // Center dot (for classic + dot option)
    if (data.style === 'classic' && data.dot) {
      this.renderDot({ ...data, style: 'dot' }, cx, cy);
    }

    this.ctx.globalAlpha = 1.0;
  }

  private renderClassic(data: CrosshairRenderData, cx: number, cy: number): void {
    if (!data.lines) return;

    for (const line of data.lines) {
      // Outline pass
      if (data.outline) {
        const ot = data.outlineThickness;
        this.ctx.fillStyle = '#000000';
        this.ctx.fillRect(
          cx + line.x - ot,
          cy + line.y - ot,
          line.width + ot * 2,
          line.height + ot * 2,
        );
      }
      // Color pass
      this.ctx.fillStyle = data.color;
      this.ctx.fillRect(cx + line.x, cy + line.y, line.width, line.height);
    }
  }

  private renderDot(data: CrosshairRenderData, cx: number, cy: number): void {
    if (!data.dot) return;

    // Outline
    if (data.outline) {
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, data.dot.radius + data.outlineThickness, 0, Math.PI * 2);
      this.ctx.fillStyle = '#000000';
      this.ctx.fill();
    }
    // Color
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, data.dot.radius, 0, Math.PI * 2);
    this.ctx.fillStyle = data.color;
    this.ctx.fill();
  }

  private renderCircle(data: CrosshairRenderData, cx: number, cy: number): void {
    if (!data.circle) return;

    // Outline
    if (data.outline) {
      this.ctx.beginPath();
      this.ctx.arc(cx, cy, data.circle.radius, 0, Math.PI * 2);
      this.ctx.lineWidth = data.circle.thickness + data.outlineThickness * 2;
      this.ctx.strokeStyle = '#000000';
      this.ctx.stroke();
    }
    // Color
    this.ctx.beginPath();
    this.ctx.arc(cx, cy, data.circle.radius, 0, Math.PI * 2);
    this.ctx.lineWidth = data.circle.thickness;
    this.ctx.strokeStyle = data.color;
    this.ctx.stroke();
  }

  dispose(): void {
    window.removeEventListener('resize', this.boundResize);
    this.canvas.remove();
  }
}
